
<template>
  <!-- 레이아웃 없는 페이지 (인사카드 등) -->
  <router-view v-if="hideLayout" :key="$route.fullPath" />

  <!-- 일반 레이아웃 -->
  <div v-else class="layout" :class="{ 'is-login': isLogin }">
    <header v-if="!isLogin" class="topbar">
      <div class="tb-left">
        <router-link to="/" class="tb-logo-link" title="홈으로 이동">
          <img src="/asset/image/CAMS.ico" alt="CAMS" class="tb-logo" />
        </router-link>
      </div>
      <div class="tb-center">
        <span v-if="pageTitle" class="tb-page-title">
          {{ pageTitle }}
        </span>
        <router-link v-else to="/" class="tb-title-link" title="홈으로 이동">
          CAMS System
        </router-link>
      </div>
      <div class="tb-right">
        <!-- 언어 선택 드롭다운 (씨앤텍차이나 메뉴에서만 표시) -->
        <div v-if="showLanguageSelector" class="language-selector">
          <button class="lang-btn" @click="toggleLangMenu" :aria-expanded="showLangMenu">
            <span class="lang-code">{{ locale }}</span>
            <i class="bi bi-chevron-down"></i>
          </button>
          <div v-if="showLangMenu" class="lang-menu" role="menu">
            <button
              v-for="opt in languageOptions"
              :key="opt.code"
              class="lang-option"
              :class="{ active: locale === opt.code }"
              role="menuitem"
              @click="selectLocale(opt.code)"
            >
              <span class="lang-opt-code">{{ opt.code }}</span>
              <span class="lang-opt-label">{{ opt.label }}</span>
            </button>
          </div>
        </div>
        <button class="user-btn" @click="toggleUserMenu" aria-haspopup="true" :aria-expanded="showUserMenu">
          <i class="bi bi-person"></i>
        </button>
        <div v-if="showUserMenu" class="user-menu" role="menu">
          <div class="list-group list-group-flush">
            <div class="list-group-item py-2 small text-muted">
              <div class="um-item">
                <span class="um-label">아이디</span>
                <span class="um-value text-body fw-semibold">{{ currentUser?.Username || '-' }}</span>
              </div>
            </div>
            <div class="list-group-item py-2 small text-muted">
              <div class="um-item">
                <span class="um-label">마지막 로그인</span>
                <span class="um-value text-body fw-semibold">{{ lastLoginText }}</span>
              </div>
            </div>
            <router-link
              v-if="currentUser && (currentUser.Id || currentUser.Username)"
              :to="{ name: 'profile-edit' }"
              class="list-group-item list-group-item-action py-2"
              role="menuitem"
              @click="showUserMenu = false"
            >내 정보 수정</router-link>
            <div class="list-group-item py-2">
              <div class="um-item">
                <span class="um-label">사이드바 자동숨김</span>
                <label class="toggle-switch">
                  <input type="checkbox" v-model="sidebarAutoHide" @change="saveSidebarSetting">
                  <span class="toggle-slider"></span>
                </label>
              </div>
            </div>
            <button type="button" class="list-group-item list-group-item-action list-group-item-danger py-2" @click="logout">로그아웃</button>
          </div>
        </div>
      </div>
    </header>
    <div class="main-row">
      <aside v-if="showShell && !isLogin" class="sidebar" :class="{ collapsed: sidebarCollapsed && sidebarAutoHide }" @mouseenter="onSidebarEnter" @mouseleave="onSidebarLeave">
        <Sidebar />
      </aside>
      <main class="content" :class="{ login: isLogin, admin: isAdmin, 'sidebar-collapsed': sidebarCollapsed && sidebarAutoHide && showShell }">
        <div v-if="sessionExpired && !isLogin" class="session-banner" role="alert">
          세션이 만료되었습니다. 다시 로그인해 주세요.
          <button class="banner-action" @click="goLogin">로그인으로 이동</button>
        </div>
        <router-view :key="$route.fullPath" />
      </main>
    </div>

    <!-- 빌드 타임 표시 (로그인 후에만) -->
    <div v-if="!isLogin && currentUser" class="build-info">
      <span v-if="frontendBuildTime" class="build-item">Frontend: {{ frontendBuildTime }}</span>
      <span v-if="backendBuildTime" class="build-item">Backend: {{ backendBuildTime }}</span>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, onBeforeUnmount, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import Sidebar from './components/Sidebar.vue';
import { currentUser, initializeUser, updateUser, pageTitle } from './store.js';
import { useLanguage } from './composables/useLanguage.js';
// import { logMenuClick, useFrontendLogger } from './composables/useFrontendLogger.js'; - 비활성화

// Frontend Logger 비활성화 (성능 최적화)
// useFrontendLogger(); - 제거됨

const route = useRoute();
const router = useRouter();
const showShell = computed(() => route.name !== 'login' && !route.meta?.hideLayout);
const isLogin = computed(() => route.name === 'login');
const isAdmin = computed(() => route.name === 'admin');
const hideLayout = computed(() => !route.name || route.meta?.hideLayout === true);
const sessionExpired = ref(sessionStorage.getItem('sessionExpired') === 'true');
const showUserMenu = ref(false);
const sidebarAutoHide = ref(false); // 사이드바 자동숨김 설정 (기본값: OFF)
const sidebarCollapsed = ref(false); // 사이드바 접힘 상태

// 빌드 타임 정보
const frontendBuildTime = ref('');
const backendBuildTime = ref('');

function saveSidebarSetting() {
  localStorage.setItem('sidebarAutoHide', sidebarAutoHide.value ? 'true' : 'false');
  // 자동숨김이 꺼지면 사이드바 펼침
  if (!sidebarAutoHide.value) {
    sidebarCollapsed.value = false;
  }
}

// 빌드 타임 정보 로드
async function loadBuildInfo() {
  try {
    // 프론트엔드 빌드 타임 (Vite 빌드 시 주입된 값)
    const frontendBuildTimeRaw = typeof __BUILD_TIME__ !== 'undefined' ? __BUILD_TIME__ : '';
    if (frontendBuildTimeRaw) {
      frontendBuildTime.value = formatBuildTime(frontendBuildTimeRaw);
    }

    // 백엔드 빌드 타임 가져오기
    const response = await fetch('/api/health');
    if (response.ok) {
      const data = await response.json();
      if (data.buildTime && data.buildTime !== 'unknown') {
        backendBuildTime.value = formatBuildTime(data.buildTime);
      }
    }
  } catch (err) {
    console.warn('[App.vue] 빌드 정보 로드 실패:', err);
  }
}

// 빌드 타임 포맷 변환 (ISO 8601 → YYYY-MM-DD HH:MM:SS)
function formatBuildTime(isoString) {
  try {
    const date = new Date(isoString);
    if (isNaN(date.getTime())) return '';

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');

    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  } catch (err) {
    console.warn('[App.vue] 빌드 타임 파싱 실패:', err);
    return '';
  }
}

function onSidebarEnter() {
  if (sidebarAutoHide.value) {
    sidebarCollapsed.value = false;
  }
}

function onSidebarLeave() {
  if (sidebarAutoHide.value) {
    sidebarCollapsed.value = true;
  }
}

// 언어 선택 관련
const { locale, languageOptions, setLocale } = useLanguage();
const showLangMenu = ref(false);
// 씨앤텍차이나 관련 페이지에서만 언어 선택기 표시
const showLanguageSelector = computed(() => {
  const routeName = route.name || '';
  return typeof routeName === 'string' && routeName.startsWith('cntechchina');
});

function toggleLangMenu() { showLangMenu.value = !showLangMenu.value; }
function selectLocale(code) {
  setLocale(code);
  showLangMenu.value = false;
}
const lastLoginText = computed(() => {
  const iso = localStorage.getItem('lastLoginAt');
  if (!iso) return '-';
  try {
    const d = new Date(iso);
    if (Number.isNaN(+d)) return '-';
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const da = String(d.getDate()).padStart(2, '0');
    const hh = String(d.getHours()).padStart(2, '0');
    const mm = String(d.getMinutes()).padStart(2, '0');
    return `${y}-${m}-${da} ${hh}:${mm}`;
  } catch { return '-'; }
});

async function logout() {
  try { await fetch('/api/auth/logout', { method: 'POST', credentials: 'include' }); } catch (e) { }
  localStorage.removeItem('token');
  sessionStorage.removeItem('sessionExpired');
  updateUser(null);
  window.location.href = '/login';
}
function goLogin() { router.replace({ name: 'login', query: { redirect: router.currentRoute.value.fullPath } }).catch(() => { }); }
function toggleUserMenu() { showUserMenu.value = !showUserMenu.value; }
function onDocClick(e) {
  const inBtn = e.target.closest && e.target.closest('.user-btn');
  const inMenu = e.target.closest && e.target.closest('.user-menu');
  if (!inBtn && !inMenu) showUserMenu.value = false;
  // 언어 메뉴 닫기
  const inLangBtn = e.target.closest && e.target.closest('.lang-btn');
  const inLangMenu = e.target.closest && e.target.closest('.lang-menu');
  if (!inLangBtn && !inLangMenu) showLangMenu.value = false;
}

// Session heartbeat: only on protected routes
let hb;
const HEARTBEAT_MS = Number(import.meta.env?.VITE_HEARTBEAT_MS || 0) || 0;
async function startHeartbeat() {
  stopHeartbeat();
  const ping = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : undefined;

      // 타임아웃 설정 (10초)
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);

      try {
        const r = await fetch('/api/auth/me', {
          credentials: 'include',
          headers,
          signal: controller.signal
        });
        clearTimeout(timeoutId);

        const j = await r.json().catch(() => null);
        if (!r.ok || !j?.ok) throw new Error(`unauthorized:${r.status}`);
        updateUser(j.user || null);
        sessionExpired.value = false;
        sessionStorage.removeItem('sessionExpired');
      } catch (e) {
        clearTimeout(timeoutId);
        // 타임아웃이나 네트워크 오류는 무시 (다음 heartbeat에서 재시도)
        if (e.name === 'AbortError') {
          console.warn('[Heartbeat] Request timeout - will retry on next heartbeat');
          return;
        }
        if (e.message && e.message.includes('Failed to fetch')) {
          console.warn('[Heartbeat] Network error - will retry on next heartbeat');
          return;
        }
        // 인증 실패인 경우에만 로그아웃 처리
        throw e;
      }
    } catch (e) {
      console.warn('[Heartbeat] Authentication failed, logging out');
      try { await fetch('/api/auth/logout', { method: 'POST', credentials: 'include' }); } catch (_) { }
      localStorage.removeItem('token');
      updateUser(null);
    }
  };
  // 초기 ping 제거 - 로그인 직후 즉시 호출하면 백엔드가 준비되지 않을 수 있음
  // await ping();
  if (HEARTBEAT_MS > 0) hb = setInterval(ping, HEARTBEAT_MS);
}
function stopHeartbeat() { if (hb) { clearInterval(hb); hb = null; } }

onMounted(() => {
  console.log('[App.vue] ========== onMounted 시작 ==========');

  // FOUC 방지: Vue 마운트 완료 표시 (index.html의 인라인 CSS가 이 클래스를 감지)
  const appEl = document.getElementById('app');
  if (appEl) {
    appEl.classList.add('vue-mounted');
    console.log('[App.vue] ✅ vue-mounted 클래스 추가 완료');
  } else {
    console.warn('[App.vue] ⚠️ #app 요소를 찾을 수 없음');
  }

  // 빌드 타임 정보 로드
  loadBuildInfo();

  // 프론트엔드 애플리케이션 시작 로그 기록
  console.log(`[FRONTEND] ✓ 프론트엔드 애플리케이션 시작`);
  console.log(`[FRONTEND] URL: ${window.location.href}`);
  console.log(`[FRONTEND] User Agent: ${navigator.userAgent}`);
  console.log(`[FRONTEND] 화면 크기: ${window.innerWidth}x${window.innerHeight}`);
  console.log(`[FRONTEND] 플랫폼: ${navigator.platform}`);
  console.log(`[FRONTEND] 시작 시각: ${new Date().toISOString()}`);

  // 프론트엔드 시작 로그 전송 비활성화 (성능 최적화)
  // S3Logger 로그 전송 완전 비활성화

  // Open Graph meta tags are statically set in index.html
  // Dynamic loading is removed because social media crawlers don't execute JavaScript
  // If you need to change OG tags, edit frontend/index.html directly

  initializeUser(); // Restore user from session storage on initial load

  // 사이드바 자동숨김 설정 로드
  const savedAutoHide = localStorage.getItem('sidebarAutoHide');
  sidebarAutoHide.value = savedAutoHide === 'true';
  sidebarCollapsed.value = sidebarAutoHide.value;

  // Load display settings
  const saved = localStorage.getItem('displaySettings');
  if (saved) {
    try {
      const settings = JSON.parse(saved);
      if (settings.sidebarFontSize) {
        document.documentElement.style.setProperty('--sidebar-font-size', `${settings.sidebarFontSize}px`);
      }
      if (settings.contentFontSize) {
        document.documentElement.style.setProperty('--content-font-size', `${settings.contentFontSize}px`);
      }
    } catch (e) {
      console.error('Failed to load display settings:', e);
    }
  }

  const token = localStorage.getItem('token');
  if (route.meta?.requiresAuth) {
    if (token) startHeartbeat();
    else {
      try { router.replace({ name: 'login', query: { redirect: router.currentRoute.value.fullPath } }); } catch {}
    }
  }
  document.addEventListener('click', onDocClick);

  // ===== [DEBUG] 화면 디밍 원인 탐지 =====
  function findOverlayElements() {
    const all = document.querySelectorAll('*');
    const suspects = [];
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    for (const el of all) {
      try {
        const st = window.getComputedStyle(el);
        const pos = st.position;
        if (pos !== 'fixed' && pos !== 'absolute') continue;
        const rect = el.getBoundingClientRect();
        // 화면 절반 이상을 덮는 요소 탐지
        if (rect.width < vw * 0.4 || rect.height < vh * 0.4) continue;
        const bg = st.backgroundColor;
        const zi = parseInt(st.zIndex) || 0;
        if (zi < 100) continue;
        suspects.push({
          tag: el.tagName,
          id: el.id,
          class: el.className,
          pos,
          zi,
          rect: `${Math.round(rect.width)}x${Math.round(rect.height)} @ (${Math.round(rect.left)},${Math.round(rect.top)})`,
          bg,
          opacity: st.opacity,
          visibility: st.visibility,
          display: st.display,
          pointerEvents: st.pointerEvents,
          outerHTML: el.outerHTML.slice(0, 200),
        });
      } catch (_) {}
    }
    return suspects;
  }

  function runOverlayCheck(label) {
    const suspects = findOverlayElements();
    if (suspects.length > 0) {
      console.warn(`[DIMMING DEBUG] ${label} — 화면 덮는 요소 발견:`, suspects);
      suspects.forEach((s, i) => console.warn(`  [${i}]`, s));
    } else {
      console.log(`[DIMMING DEBUG] ${label} — 화면 덮는 요소 없음`);
    }
  }

  // 페이지 로드 직후 체크
  setTimeout(() => runOverlayCheck('onMounted(500ms)'), 500);
  setTimeout(() => runOverlayCheck('onMounted(2000ms)'), 2000);

  // route 변경 시 체크 (watch로 처리)
  const stopOverlayWatch = watch(() => route.fullPath, (newPath) => {
    setTimeout(() => runOverlayCheck(`route 변경 후 → ${newPath}`), 1000);
  });

  // MutationObserver: DOM 변화 감지
  const overlayObserver = new MutationObserver(() => {
    const suspects = findOverlayElements();
    if (suspects.length > 0) {
      console.warn('[DIMMING DEBUG] DOM 변화 감지 — 화면 덮는 요소:', suspects);
    }
  });
  overlayObserver.observe(document.body, { childList: true, subtree: false });
  // ===== [DEBUG END] =====
});
watch(() => route.meta?.requiresAuth, (need) => {
  const token = localStorage.getItem('token');
  if (need) { if (token) startHeartbeat(); } else stopHeartbeat();
  if (!need) sessionExpired.value = false;
});

// 메뉴 클릭 로그 비활성화 (성능 최적화)
// watch(() => route.fullPath, ...) - 제거됨

onBeforeUnmount(() => { stopHeartbeat(); document.removeEventListener('click', onDocClick); });

// 로그인 화면 배경 로고 제거됨 (성능 최적화)
</script>

<style>
.layout{ display:flex; flex-direction:column; min-height:100vh; padding-top:52px; }
.layout.is-login { padding-top: 0 !important; margin-top: 0 !important; min-height: 100vh; height: 100vh; overflow: hidden; }
.main-row{ display:flex; flex:1 1 auto; min-height:0; position: relative; }
.layout.is-login .main-row { flex: none; height: 100vh; margin-top: 0 !important; }
.layout.is-login .sidebar { display: none !important; }
.sidebar{
  width:220px; min-width:220px; max-width:220px; flex-shrink:0;
  background:#000; color:#fff; border-right:1px solid #222;
  display:flex; flex-direction:column;
  position: fixed; left: 0; top: 52px; bottom: 0; z-index: 20;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  overflow-y: auto; overflow-x: hidden;
}
.sidebar.collapsed{
  transform: translateX(-210px);
  box-shadow: none;
}
.sidebar:not(.collapsed){
  transform: translateX(0);
  box-shadow: 2px 0 8px rgba(0,0,0,0.15);
}
.sidebar.collapsed::after {
  content: '☰';
  position: absolute;
  right: -30px;
  top: 10px;
  width: 30px;
  height: 40px;
  background: rgba(0, 0, 0, 0.7);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  border-radius: 0 8px 8px 0;
  cursor: pointer;
}
.menu-title{ padding:12px 16px; color:#ddd; border-bottom:1px solid #222; font-weight:700; font-size:1.125rem; }
.content{
  flex:1 1 auto; min-width:0; padding:12px 16px 16px 0px; overflow-x:hidden;
  margin-left: 30px; transition: margin-left 0.3s ease;
}
.content.sidebar-collapsed{
  margin-left: -180px;
}
.content.login{
  margin-left: 0 !important;
  padding: 0 !important;
}
.topbar{ position:fixed; top:0; left:0; right:0; z-index:30; height:52px; padding:0 16px; background:#000; color:#fff; display:flex; align-items:center; }
.tb-left{ width:44px; display:flex; align-items:center; justify-content:center; }
.tb-logo-link{ display:flex; align-items:center; justify-content:center; cursor:pointer; transition:opacity 0.2s; }
.tb-logo-link:hover{ opacity:0.8; }
.tb-logo{ width:28px; height:28px; }
.tb-center{ flex:1 1 auto; text-align:center; font-weight:600; letter-spacing:.4px; font-size:1rem; }
.tb-title-link{ color:#fff; text-decoration:none; cursor:pointer; transition:opacity 0.2s; }
.tb-title-link:hover{ opacity:0.8; color:#fff; }
.tb-page-title{ color:#fff; font-weight:600; letter-spacing:.4px; font-size:1.125rem; }
.tb-right{ width:auto; min-width:44px; position:relative; display:flex; align-items:center; justify-content:flex-end; gap:12px; }
/* 언어 선택기 스타일 */
.language-selector{ position:relative; }
.lang-btn{ display:flex; align-items:center; gap:4px; padding:4px 10px; border-radius:6px; border:1px solid rgba(255,255,255,.4); background:rgba(255,255,255,.1); color:#fff; cursor:pointer; font-size:0.875rem; transition:all 0.2s; }
.lang-btn:hover{ background:rgba(255,255,255,.2); border-color:rgba(255,255,255,.6); }
.lang-code{ font-weight:600; }
.lang-btn .bi{ font-size:0.7rem; opacity:0.8; }
.lang-menu{ position:absolute; right:0; top:36px; background:#fff; color:#111; border:1px solid #e5e7eb; border-radius:8px; min-width:120px; box-shadow:0 8px 20px rgba(0,0,0,.15); padding:4px 0; overflow:hidden; z-index:100; }
.lang-option{ display:flex; align-items:center; gap:10px; width:100%; padding:8px 14px; border:none; background:transparent; color:#333; cursor:pointer; text-align:left; font-size:0.875rem; transition:background 0.15s; }
.lang-option:hover{ background:#f3f4f6; }
.lang-option.active{ background:#e0f2fe; color:#0369a1; }
.lang-opt-code{ font-weight:600; min-width:28px; }
.lang-opt-label{ color:#6b7280; }
.lang-option.active .lang-opt-label{ color:#0369a1; }

.user-btn{ width:30px; height:30px; border-radius:50%; border:1px solid rgba(255,255,255,.6); background:rgba(255,255,255,.15); color:#fff; display:flex; align-items:center; justify-content:center; cursor:pointer; }
.user-btn .bi{ font-size:1.1rem; }
.user-menu{ position:absolute; right:0; top:42px; background:#fff; color:#111; border:1px solid #e5e7eb; border-radius:8px; min-width:260px; box-shadow:0 12px 28px rgba(0,0,0,.12); padding:0; text-align:left; overflow:hidden; /* label width control */ --um-label-w: 120px; }
.um-item{ display:flex; align-items:center; gap:8px; }
.um-label{ color:#6b7280; font-size:.9rem; flex:0 0 var(--um-label-w); max-width: var(--um-label-w); }
.um-value{ font-weight:600; }
/* 토글 스위치 */
.toggle-switch{ position:relative; display:inline-block; width:40px; height:22px; }
.toggle-switch input{ opacity:0; width:0; height:0; }
.toggle-slider{ position:absolute; cursor:pointer; top:0; left:0; right:0; bottom:0; background-color:#ccc; transition:.3s; border-radius:22px; }
.toggle-slider:before{ position:absolute; content:""; height:16px; width:16px; left:3px; bottom:3px; background-color:white; transition:.3s; border-radius:50%; }
.toggle-switch input:checked + .toggle-slider{ background-color:#0d6efd; }
.toggle-switch input:checked + .toggle-slider:before{ transform:translateX(18px); }
.session-banner{ position:sticky; top:0; z-index:10; margin:0 0 12px 0; padding:10px 12px; background:#fff3cd; color:#664d03; border-bottom:1px solid #ffe69c; display:flex; align-items:center; justify-content:space-between; gap:12px; }
.session-banner .banner-action{ border:1px solid #0d6efd; background:#0d6efd; color:#fff; padding:6px 10px; border-radius:6px; cursor:pointer; }
.content.login{
  display:flex; align-items:center; justify-content:center; padding:0; min-height:100vh;
  /* WebP 우선 (225KB), fallback으로 JPG (344KB) 사용 */
  background: url('/asset/image/bg.webp') center center / cover no-repeat;
  background: image-set(
    url('/asset/image/bg.webp') type('image/webp'),
    url('/asset/image/bg.jpg') type('image/jpeg')
  ) center center / cover no-repeat;
  position: relative;
}

.content.login > section.login {
  color: #1e293b;
}

.content.login > section.login input {
  color: #1e293b !important;
}

.content.login > section.login label {
  color: #1e293b !important;
}

.content.login > section.login button {
  color: #ffffff !important;
}

.content.login > section.login .footer-logo {
  color: initial;
}

.content.login > section.login .no-underline {
  color: #ffffff !important;
}

.content.login > section.login .no-underline:hover,
.content.login > section.login .no-underline:focus {
  color: rgba(255, 255, 255, 0.8) !important;
}

/* Modal 팝업은 원래 색상 유지 */
.modal-backdrop-custom {
  color: initial;
}

.modal-backdrop-custom .modal-header {
  color: #fff !important;
}

.modal-backdrop-custom .modal-body {
  color: #000 !important;
}

.modal-backdrop-custom .modal-body label,
.modal-backdrop-custom .modal-body input,
.modal-backdrop-custom .modal-body small {
  color: #000 !important;
}

.modal-backdrop-custom .text-danger {
  color: #dc3545 !important;
}

.modal-backdrop-custom .modal-body button.btn-primary,
.modal-backdrop-custom .modal-body button[type="submit"] {
  color: #fff !important;
  background-color: #0d6efd !important;
}

/* 모바일에서는 배경 이미지 제거하고 그라디언트만 표시 */
@media (max-width: 768px) {
  .content.login {
    background: linear-gradient(180deg, rgba(245,250,255,.92) 0%, rgba(238,245,255,.92) 100%) !important;
  }
}
.layout .content:not(.login){ background:#ffffff !important; }
.layout .content.admin{ background:#ffffff !important; }
/* Font size control with CSS variables */
:root {
  --sidebar-font-size: 15px;  /* 기본값: 15px (16px - 1px) */
  --content-font-size: 13px;  /* 기본값: 13px (14.4px - 1.4px) */
}
.layout{ font-size: var(--content-font-size); }
.layout .sidebar{ font-size: var(--sidebar-font-size); }
/* Apply content font size to all major elements */
.layout .content,
.layout .content .card,
.layout .content .card-body,
.layout .content .form-control,
.layout .content .form-label,
.layout .content .form-text,
.layout .content .btn,
.layout .content label,
.layout .content p,
.layout .content div,
.layout .content span,
.layout .content td,
.layout .content th,
.layout .content input,
.layout .content textarea,
.layout .content select {
  font-size: inherit;
}

/* Element Plus 컴포넌트에 폰트 크기 강제 적용 */
.layout .content .el-tabs,
.layout .content .el-tab-pane,
.layout .content .el-tabs__header,
.layout .content .el-tabs__nav,
.layout .content .el-tabs__item,
.layout .content .el-button,
.layout .content .el-button span,
.layout .content .el-input,
.layout .content .el-input__inner,
.layout .content .el-input__wrapper,
.layout .content .el-select,
.layout .content .el-select .el-input__inner,
.layout .content .el-date-picker,
.layout .content .el-checkbox,
.layout .content .el-checkbox__label,
.layout .content .el-checkbox-group,
.layout .content .el-radio,
.layout .content .el-radio__label,
.layout .content .el-radio-group,
.layout .content .el-form-item,
.layout .content .el-form-item__label {
  font-size: var(--content-font-size) !important;
}

/* Element Plus Tab 내부의 모든 요소에 폰트 크기 적용 */
.layout .content .el-tab-pane table,
.layout .content .el-tab-pane table th,
.layout .content .el-tab-pane table td,
.layout .content .el-tab-pane .table,
.layout .content .el-tab-pane .table th,
.layout .content .el-tab-pane .table td,
.layout .content .el-tab-pane label,
.layout .content .el-tab-pane .form-label,
.layout .content .el-tab-pane div,
.layout .content .el-tab-pane span,
.layout .content .el-tab-pane p,
.layout .content .el-tab-pane input,
.layout .content .el-tab-pane select,
.layout .content .el-tab-pane textarea,
.layout .content .el-tab-pane button {
  font-size: var(--content-font-size) !important;
}

/* Tab 레이블 크기 조정 */
.layout .content .el-tabs__item {
  font-size: var(--content-font-size) !important;
  padding: calc(var(--content-font-size) * 0.8) calc(var(--content-font-size) * 1.2) !important;
}

/* 모든 label 요소 일관되게 적용 */
.layout .content label,
.layout .content .form-label {
  font-size: var(--content-font-size) !important;
}
/* Table row height adjustment based on font size */
.layout .table > :not(caption) > * > * {
  padding: calc(var(--content-font-size) * 0.5) calc(var(--content-font-size) * 0.6);
}
.layout .table-sm > :not(caption) > * > * {
  padding: calc(var(--content-font-size) * 0.35) calc(var(--content-font-size) * 0.4);
}
/* Unified page header layout and spacing */
.content .page-header{ display:block; text-align:left; margin: 8px 0 12px; padding:0; }
.content .page-header .page-title{ margin:0; line-height:1.3; }
/* Consistent gap between header and tables */
.content .page-header + .table-responsive{ margin-top: 8px; }
.content .page-header + form,
.content .page-header + .card{ margin-top: 8px; }
/* Hide page titles in content area (they're now shown in header) */
.content .page-title,
.content h3.page-title,
.content h2.page-title{ display:none !important; }

/* 빌드 정보 표시 (페이지 하단 고정) */
.build-info {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: rgba(0, 0, 0, 0.75);
  color: rgba(255, 255, 255, 0.7);
  padding: 4px 12px;
  font-size: 0.7rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  z-index: 1000;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(4px);
}

.build-item {
  font-family: 'Courier New', monospace;
  white-space: nowrap;
}

/* 로그인 화면에서는 빌드 정보 숨김 */
.layout.is-login .build-info {
  display: none;
}
</style>
